import React from 'react';

export const CookiePage: React.FC = () => {
    return (
        <div className="min-h-screen bg-[var(--surface-1)]">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-20 animate-fade-in">
                <div className="max-w-4xl mx-auto">
                    <div className="text-center mb-12">
                        <h1 className="text-4xl sm:text-5xl font-bold text-[var(--text-1)] font-serif mb-4">
                            Cookie Policy
                        </h1>
                        <p className="text-[var(--text-3)]">Last updated: January 15, 2025</p>
                    </div>

                    <div className="bg-[var(--surface-2)] rounded-3xl shadow-xl border border-[var(--surface-4)] p-8 sm:p-12 space-y-8">
                        <section>
                            <h2 className="text-2xl font-bold text-[var(--text-1)] mb-4">1. What Are Cookies?</h2>
                            <p className="text-[var(--text-2)] leading-relaxed">
                                Cookies are small text files that are placed on your computer or mobile device when you visit
                                a website. They are widely used to make websites work more efficiently and provide information
                                to website owners. Cookies help us understand how you use our service and improve your experience.
                            </p>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold text-[var(--text-1)] mb-4">2. How We Use Cookies</h2>
                            <p className="text-[var(--text-2)] leading-relaxed mb-4">
                                CompareLens uses cookies and similar technologies to:
                            </p>
                            <ul className="list-disc list-inside text-[var(--text-2)] space-y-2 ml-4">
                                <li>Remember your preferences and settings</li>
                                <li>Store your search history locally for quick access</li>
                                <li>Understand how you interact with our platform</li>
                                <li>Improve the performance and functionality of our service</li>
                                <li>Analyze traffic and usage patterns</li>
                                <li>Enhance security and prevent fraud</li>
                            </ul>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold text-[var(--text-1)] mb-4">3. Types of Cookies We Use</h2>

                            <div className="space-y-6">
                                <div className="p-5 bg-[var(--surface-3)] rounded-xl border border-[var(--surface-4)]">
                                    <h3 className="font-bold text-lg text-[var(--text-1)] mb-3">Essential Cookies</h3>
                                    <p className="text-[var(--text-2)] leading-relaxed mb-2">
                                        These cookies are necessary for the website to function properly. They enable core
                                        functionality such as security, network management, and accessibility.
                                    </p>
                                    <p className="text-sm text-[var(--text-3)] italic">
                                        Examples: Session cookies, security cookies
                                    </p>
                                </div>

                                <div className="p-5 bg-[var(--surface-3)] rounded-xl border border-[var(--surface-4)]">
                                    <h3 className="font-bold text-lg text-[var(--text-1)] mb-3">Functional Cookies</h3>
                                    <p className="text-[var(--text-2)] leading-relaxed mb-2">
                                        These cookies allow the website to remember choices you make and provide enhanced,
                                        personalized features. The information collected is usually anonymized.
                                    </p>
                                    <p className="text-sm text-[var(--text-3)] italic">
                                        Examples: Search history storage (localStorage), user preferences
                                    </p>
                                </div>

                                <div className="p-5 bg-[var(--surface-3)] rounded-xl border border-[var(--surface-4)]">
                                    <h3 className="font-bold text-lg text-[var(--text-1)] mb-3">Analytics Cookies</h3>
                                    <p className="text-[var(--text-2)] leading-relaxed mb-2">
                                        These cookies help us understand how visitors interact with our website by collecting
                                        and reporting information anonymously. This helps us improve our service.
                                    </p>
                                    <p className="text-sm text-[var(--text-3)] italic">
                                        Examples: Page views, time spent on pages, bounce rate tracking
                                    </p>
                                </div>

                                <div className="p-5 bg-[var(--surface-3)] rounded-xl border border-[var(--surface-4)]">
                                    <h3 className="font-bold text-lg text-[var(--text-1)] mb-3">Performance Cookies</h3>
                                    <p className="text-[var(--text-2)] leading-relaxed mb-2">
                                        These cookies collect information about how you use our website to help us improve
                                        its performance and user experience.
                                    </p>
                                    <p className="text-sm text-[var(--text-3)] italic">
                                        Examples: Loading time tracking, error monitoring
                                    </p>
                                </div>
                            </div>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold text-[var(--text-1)] mb-4">4. Local Storage</h2>
                            <p className="text-[var(--text-2)] leading-relaxed mb-3">
                                In addition to cookies, we use browser local storage to save your search history. This data
                                is stored entirely on your device and is not transmitted to our servers. Key features:
                            </p>
                            <ul className="list-disc list-inside text-[var(--text-2)] space-y-2 ml-4">
                                <li>Stores up to 10 recent laptop searches</li>
                                <li>Remains on your device until you clear it manually</li>
                                <li>Can be deleted at any time using the Clear History button</li>
                                <li>Only accessible by CompareLens on your device</li>
                            </ul>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold text-[var(--text-1)] mb-4">5. Third-Party Cookies</h2>
                            <p className="text-[var(--text-2)] leading-relaxed mb-3">
                                Some cookies on our website are placed by third-party services we use:
                            </p>
                            <div className="space-y-3 ml-4">
                                <div>
                                    <h3 className="font-semibold text-[var(--text-1)] mb-1">Google Services</h3>
                                    <p className="text-[var(--text-2)] text-sm">
                                        We use Google Gemini AI and Google Search to provide our core functionality.
                                        These services may set their own cookies. Please refer to Google's Privacy Policy
                                        for more information.
                                    </p>
                                </div>
                                <div>
                                    <h3 className="font-semibold text-[var(--text-1)] mb-1">External Links</h3>
                                    <p className="text-[var(--text-2)] text-sm">
                                        When you click on external links to retailers or product pages, those websites may
                                        set their own cookies. We have no control over these third-party cookies.
                                    </p>
                                </div>
                            </div>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold text-[var(--text-1)] mb-4">6. Managing Your Cookie Preferences</h2>
                            <p className="text-[var(--text-2)] leading-relaxed mb-4">
                                You have several options to manage or disable cookies:
                            </p>

                            <div className="space-y-4">
                                <div>
                                    <h3 className="font-semibold text-[var(--text-1)] mb-2">Browser Settings</h3>
                                    <p className="text-[var(--text-2)] text-sm mb-2">
                                        Most web browsers allow you to control cookies through their settings. You can:
                                    </p>
                                    <ul className="list-disc list-inside text-[var(--text-2)] text-sm space-y-1 ml-4">
                                        <li>Block all cookies</li>
                                        <li>Allow only first-party cookies</li>
                                        <li>Delete cookies when you close your browser</li>
                                        <li>Clear all cookies currently stored</li>
                                    </ul>
                                </div>

                                <div className="p-4 bg-[var(--warning-bg)] border border-[var(--warning)]/20 rounded-lg">
                                    <p className="text-sm text-[var(--warning-text)] flex items-start gap-2">
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                        </svg>
                                        <span>
                                            Please note that blocking or deleting cookies may impact your experience on CompareLens.
                                            Some features, such as search history, may not work properly without cookies enabled.
                                        </span>
                                    </p>
                                </div>

                                <div>
                                    <h3 className="font-semibold text-[var(--text-1)] mb-2">Browser-Specific Instructions</h3>
                                    <ul className="list-disc list-inside text-[var(--text-2)] text-sm space-y-1 ml-4">
                                        <li><strong>Chrome:</strong> Settings &gt; Privacy and security &gt; Cookies and other site data</li>
                                        <li><strong>Firefox:</strong> Settings &gt; Privacy & Security &gt; Cookies and Site Data</li>
                                        <li><strong>Safari:</strong> Preferences &gt; Privacy &gt; Cookies and website data</li>
                                        <li><strong>Edge:</strong> Settings &gt; Cookies and site permissions &gt; Cookies and site data</li>
                                    </ul>
                                </div>
                            </div>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold text-[var(--text-1)] mb-4">7. Cookie Duration</h2>
                            <div className="space-y-3">
                                <div>
                                    <h3 className="font-semibold text-[var(--text-1)] mb-1">Session Cookies</h3>
                                    <p className="text-[var(--text-2)] text-sm">
                                        These temporary cookies are deleted when you close your browser.
                                    </p>
                                </div>
                                <div>
                                    <h3 className="font-semibold text-[var(--text-1)] mb-1">Persistent Cookies</h3>
                                    <p className="text-[var(--text-2)] text-sm">
                                        These cookies remain on your device until they expire or you delete them manually.
                                        Our search history stored in localStorage persists until you clear it.
                                    </p>
                                </div>
                            </div>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold text-[var(--text-1)] mb-4">8. Updates to This Policy</h2>
                            <p className="text-[var(--text-2)] leading-relaxed">
                                We may update this Cookie Policy from time to time to reflect changes in technology, legislation,
                                or our data practices. We will post any changes on this page with an updated revision date.
                                We encourage you to review this policy periodically to stay informed about how we use cookies.
                            </p>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold text-[var(--text-1)] mb-4">9. Contact Us</h2>
                            <p className="text-[var(--text-2)] leading-relaxed mb-4">
                                If you have any questions about our use of cookies or this Cookie Policy, please contact us:
                            </p>
                            <div className="p-4 bg-[var(--surface-3)] rounded-lg">
                                <p className="text-[var(--text-2)]">
                                    Email: <a href="mailto:davesaumya24@gmail.com" className="text-[var(--brand)] hover:underline">davesaumya24@gmail.com</a>
                                </p>
                            </div>
                        </section>
                    </div>

                    <div className="text-center mt-8">
                        <a
                            href="/"
                            className="text-[var(--brand)] hover:text-[var(--brand-hover)] font-semibold transition-colors"
                        >
                            Back to Home
                        </a>
                    </div>
                </div>
            </div>
        </div>
    );
};
